import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  Modal,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { BarChart } from "@mui/x-charts/BarChart";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Controller, useForm } from "react-hook-form";
import { useMutation } from "react-query";
import { useLocation } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useNotification from "../../hooks/useNotification";
import category from "../../services/category";
import { rupiah } from "../../utils/currency";
import Ticket from "../ticket/Ticket";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "90%", sm: 600 },
  maxHeight: "90vh",
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: { xs: 2, sm: 4 },
  overflow: "auto",
};

const chartSetting = { height: 300 };

const locations = [
  "Surabaya",
  "Sidoarjo",
  "Malang & Batu",
  "Gresik",
  "Kediri",
  "Lamongan",
  "Pasuruan",
  "Mojokerto",
  "Jember",
  "Banyuwangi",
  "Probolinggo / Pasuruan",
  "Jawa Timur Lainnya",
  "Jawa Tengah",
  "D.I. Yogyakarta",
  "Jawa Barat",
  "Jabodetabek",
  "Banten",
  "Bali",
  "Kalimantan",
  "Sulawesi",
  "Sumatera",
  "Nusa Tenggara",
  "Maluku",
  "Papua",
  "Wilayah Indonesia Lainnya",
  "Malaysia",
  "Thailand",
  "Singapore",
  "Negara Lainnya",
];

interface InfoCardProps {
  title: string;
  value: string | number;
  color?: string;
}

const InfoCard = ({ title, value, color = "primary.main" }: InfoCardProps) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      borderRadius: 2,
      bgcolor: "background.paper",
      border: "1px solid",
      borderColor: "divider",
    }}
  >
    <Typography variant="body2" color="text.secondary" gutterBottom>
      {title}
    </Typography>
    <Typography variant="h6" color={color} fontWeight="700">
      {value}
    </Typography>
  </Paper>
);

export default function CategoryDetail() {
  const location = useLocation();
  const path = location.pathname.split("/");
  const category_id = path.pop() || "";
  const event_slug = location.state?.event_slug;
  const notification = useNotification();
  const [data, setData] = useState({
    id: category_id,
    event_id: 0,
    name: "",
    price: 0,
    event_name: "",
    total_ticket: 0,
    total_available: 0,
    total_booked: 0,
    total_sold: 0,
    total_invitation: 0,
    is_active: false,
    show_masked_sold: false,
    masked_sold: 0,
    masked_invitation: 0,
    masked_available: 0,
  });
  const [ticketStats, setTicketStats] = useState([
    { grouped_days: "", count: "0" },
  ]);

  const formInvitation = useForm({
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      category_id: Number(category_id),
      event_id: 0,
      gender: "male",
      location: "Surabaya",
      is_complimentary: false,
      quantity: 1,
    },
  });

  const [open, setOpen] = useState(false);
  const [openBulk, setOpenBulk] = useState(false);

  const formBulk = useForm({
    defaultValues: {
      category_id: Number(category_id),
      total_ticket: 1,
    },
  });

  const handleCloseModal = () => {
    setOpen(false);
    formInvitation.reset();
    formInvitation.setValue("category_id", Number(category_id));
    formInvitation.setValue("event_id", data.event_id);
  };

  const handleCloseBulkModal = () => {
    setOpenBulk(false);
    formBulk.reset();
    formBulk.setValue("category_id", Number(category_id));
  };

  useEffect(() => {
    category.viewCategoryDetail(category_id).then((response: any) => {
      setData({
        id: category_id,
        event_id: response?.data?.category?.event_id ?? 0,
        name: response?.data?.category?.name ?? "",
        price: response?.data?.category?.price ?? 0,
        event_name: response?.data?.category?.event_name ?? "",
        total_ticket: response?.data?.total_ticket ?? 0,
        total_available: response?.data?.total_available ?? 0,
        total_booked: response?.data?.total_booked ?? 0,
        total_sold: response?.data?.total_sold ?? 0,
        total_invitation: response?.data?.total_invitation ?? 0,
        is_active: response?.data?.category?.is_active ?? false,
        show_masked_sold: response?.data?.category?.show_masked_sold ?? false,
        masked_sold: response?.data?.category?.masked_sold ?? 0,
        masked_invitation: response?.data?.category?.masked_invitation ?? 0,
        masked_available: response?.data?.category?.masked_available ?? 0,
      });
      formInvitation.setValue(
        "event_id",
        response?.data?.category?.event_id ?? 0
      );
      if (response?.data?.ticket_stats?.length > 0) {
        for (const i of response.data.ticket_stats) {
          const date = new Date(i.grouped_days);
          i.grouped_days = `${date.getMonth() + 1}-${date.getDate()}`;
          i.count = Number(i.count);
        }
        setTicketStats(response.data.ticket_stats);
      }
    });
  }, [category_id]);

  const createInvitation = useMutation(
    "createInvitation",
    category.createInvitation,
    {
      onSuccess: () => {
        formInvitation.reset();
        formInvitation.setValue("category_id", Number(category_id));
        formInvitation.setValue("event_id", data.event_id);
        notification.onOpen({
          message: "Invitation was successfully created!",
          type: "success",
          position: "top",
        });
      },
      onError: (error: any) => {
        notification.onOpen({
          message:
            error?.response?.data?.error !== ""
              ? error.response.data.error
              : "Something went wrong",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const generateBulkLinks = useMutation(
    "generateBulkLinks",
    (data: { category_id: number; total_ticket: number }) =>
      category.generateTicketLinks(data),
    {
      onSuccess: () => {
        setOpenBulk(false);
        formBulk.reset();
        formBulk.setValue("category_id", Number(category_id));
        notification.onOpen({
          message: "Bulk invitation links generated successfully!",
          type: "success",
          position: "top",
        });
      },
      onError: (error: any) => {
        notification.onOpen({
          message:
            error?.response?.data?.error !== ""
              ? error.response.data.error
              : "Something went wrong",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const downloadInvitations = useMutation(
    "downloadInvitations",
    () => category.downloadInvitations(category_id),
    {
      onSuccess: (response: any) => {
        // Create blob and download file
        notification.onOpen({
          message: "Invitations downloaded successfully!",
          type: "success",
          position: "top",
        });
      },
      onError: (error: any) => {
        notification.onOpen({
          message:
            error?.response?.data?.error !== ""
              ? error.response.data.error
              : "Something went wrong",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const onSubmit = (values: any) => {
    createInvitation.mutate(values);
    setOpen(false);
  };

  const onBulkSubmit = (values: any) => {
    generateBulkLinks.mutate(values);
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <MainLayout>
      <Helmet>
        <title>{data.name} - Category Details</title>
      </Helmet>

      <Breadcrumb
        breadcrumbs={[
          { label: "Event", href: "/admin/event" },
          { label: data.event_name, href: `/admin/event/${event_slug}` },
        ]}
        label={data.name}
      />

      <ProtectedPermissionPage acceptPermissions={[3]}>
        <Box sx={{ mt: 3 }}>
          {/* Category Header */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 2,
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
              spacing={2}
            >
              <Box>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  {data.name}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  {data.event_name}
                </Typography>
              </Box>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button
                  variant="outlined"
                  onClick={() => setOpenBulk(true)}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    px: 3,
                  }}
                >
                  Generate Bulk Links
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => downloadInvitations.mutate()}
                  disabled={downloadInvitations.isLoading}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    px: 3,
                  }}
                >
                  {downloadInvitations.isLoading
                    ? "Downloading..."
                    : "Download Invitations"}
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setOpen(true)}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    px: 3,
                  }}
                >
                  Create Invitation
                </Button>
              </Stack>
            </Stack>
          </Paper>

          {/* Stats Grid */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Stack spacing={2}>
                <InfoCard
                  title="Price"
                  value={rupiah(data.price)}
                  color="success.main"
                />
                <InfoCard
                  title="Status"
                  value={data.is_active ? "Active" : "Inactive"}
                  color={data.is_active ? "success.main" : "error.main"}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack spacing={2}>
                <InfoCard title="Total Tickets" value={data.total_ticket} />
                <InfoCard
                  title="Available Tickets"
                  value={!data.show_masked_sold ? data.total_available : data.masked_available}
                  color={
                    data.total_available > 0 ? "success.main" : "warning.main"
                  }
                />
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack spacing={2}>
                <InfoCard
                  title="Tickets Sold Online"
                  value={`${
                    !data.show_masked_sold ? data.total_sold : data.masked_sold
                  } (${(((!data.show_masked_sold ? data.total_sold : data.masked_sold) / data.total_ticket) * 100).toFixed(
                    1
                  )}%)`}
                />
                <InfoCard
                  title="Total Sold Offline"
                  value={!data.show_masked_sold  ? data.total_invitation : data.masked_invitation}
                />
              </Stack>
            </Grid>
          </Grid>

          {/* Tickets Section */}
          <Box sx={{ mt: 4 }}>
            <Ticket id={category_id} isMobile={isMobile} />
          </Box>
          <Grid marginTop="10px"></Grid>
          <Typography variant="h3">Sale Statistics</Typography>
          <BarChart
            dataset={ticketStats}
            xAxis={[
              {
                scaleType: "band",
                dataKey: "grouped_days",
                label: "Month-Date",
              },
            ]}
            series={[{ dataKey: "count", label: "Total" }]}
            {...chartSetting}
          />
        </Box>

        {/* Invitation Modal */}
        <Modal
          open={open}
          onClose={handleCloseModal}
          aria-labelledby="create-invitation-modal"
        >
          <Box sx={modalStyle}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Create Invitation
            </Typography>

            <Stack spacing={3} sx={{ mt: 3 }}>
              <FormControl fullWidth>
                <FormLabel required>Name</FormLabel>
                <Controller
                  name="name"
                  control={formInvitation.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      placeholder="Enter invitee's name"
                      fullWidth
                      inputProps={{ maxLength: 30 }}
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth>
                <FormLabel required>Email</FormLabel>
                <Controller
                  name="email"
                  control={formInvitation.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="email"
                      size="small"
                      placeholder="Enter email address"
                      fullWidth
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth>
                <FormLabel required>Phone Number</FormLabel>
                <Controller
                  name="phone"
                  control={formInvitation.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      placeholder="Enter phone number"
                      fullWidth
                    />
                  )}
                />
              </FormControl>

              <Box>
                <FormControl>
                  <FormLabel required>Quantity</FormLabel>
                  <Controller
                    name="quantity"
                    control={formInvitation.control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="number"
                        size="small"
                        placeholder="Enter quantity"
                        fullWidth
                        inputProps={{ min: 1, max: 5 }}
                      />
                    )}
                  />
                </FormControl>
                <FormControl sx={{ mt: 3, ml: 2 }}>
                  <FormControlLabel
                    label="Is Complimentary"
                    control={
                      <Controller
                        name="is_complimentary"
                        control={formInvitation.control}
                        render={({ field }) => (
                          <Checkbox
                            {...field}
                            checked={
                              formInvitation.getValues().is_complimentary
                            }
                          />
                        )}
                      />
                    }
                  />
                </FormControl>
              </Box>

              <FormControl fullWidth>
                <FormLabel required>Gender</FormLabel>
                <Controller
                  name="gender"
                  control={formInvitation.control}
                  render={({ field }) => (
                    <ButtonGroup
                      fullWidth
                      size="small"
                      sx={{
                        "& .MuiButton-root": {
                          textTransform: "none",
                          py: 1,
                        },
                      }}
                    >
                      <Button
                        variant={
                          field.value === "male" ? "contained" : "outlined"
                        }
                        onClick={() => field.onChange("male")}
                      >
                        Male
                      </Button>
                      <Button
                        variant={
                          field.value === "female" ? "contained" : "outlined"
                        }
                        onClick={() => field.onChange("female")}
                      >
                        Female
                      </Button>
                    </ButtonGroup>
                  )}
                />
              </FormControl>

              <FormControl fullWidth>
                <FormLabel required>Location</FormLabel>
                <Controller
                  name="location"
                  control={formInvitation.control}
                  render={({ field, fieldState }) => (
                    <Box>
                      <Select
                        {...field}
                        size="small"
                        error={Boolean(fieldState.error?.message)}
                        fullWidth
                      >
                        {locations.map((item) => (
                          <MenuItem value={item} key={item}>
                            {item}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText
                        error={Boolean(fieldState.error?.message)}
                      >
                        {fieldState.error?.message}
                      </FormHelperText>
                    </Box>
                  )}
                />
              </FormControl>

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={handleCloseModal}
                  sx={{ textTransform: "none" }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={formInvitation.handleSubmit(onSubmit)}
                  sx={{ textTransform: "none" }}
                >
                  Create Invitation
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Modal>

        {/* Bulk Generation Modal */}
        <Modal
          open={openBulk}
          onClose={handleCloseBulkModal}
          aria-labelledby="bulk-generation-modal"
        >
          <Box sx={modalStyle}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Generate Bulk Invitation Links
            </Typography>

            <Stack spacing={3} sx={{ mt: 3 }}>
              <FormControl fullWidth>
                <FormLabel required>Total Tickets</FormLabel>
                <Controller
                  name="total_ticket"
                  control={formBulk.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="number"
                      size="small"
                      placeholder="Enter number of tickets to generate"
                      fullWidth
                      inputProps={{ min: 1, max: 1000 }}
                    />
                  )}
                />
              </FormControl>

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={handleCloseBulkModal}
                  sx={{ textTransform: "none" }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={formBulk.handleSubmit(onBulkSubmit)}
                  disabled={generateBulkLinks.isLoading}
                  sx={{ textTransform: "none" }}
                >
                  {generateBulkLinks.isLoading
                    ? "Generating..."
                    : "Generate Links"}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Modal>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
